import { Ionicons } from '@expo/vector-icons';
import React, { useState, useRef, useEffect } from "react";
import { View, Platform, Dimensions, Alert, TouchableOpacity, BackHandler } from "react-native";
import { Animated, Easing } from 'react-native';

import ScreenWrapper from "../../components/ScreenWrapper";
import { useAppTheme } from '../../design-system/ThemeProvider';
import { useLanguageStore } from "../../features/quiz-core/languageStore";
import { supabase } from "../../lib/supabase";
import { useGuestUser } from '../contexts/GuestUserContext';
import { authService } from '../services/authService';

import AppleSignInButton from './AuthScreen/AppleSignInButton';
import AuthForm from './AuthScreen/AuthForm';
import EmailConfirmationMessage from './AuthScreen/EmailConfirmationMessage';
import Footer from './AuthScreen/Footer';
import LogoHeader from './AuthScreen/LogoHeader';
import SocialButton from './AuthScreen/SocialButton';
import { createAuthStyles } from './AuthScreen.styles';
import useReducedMotion from './useReducedMotion';
import { redactUrl } from '../utils/redactUrl';
import { uiText } from '../utils/uiText';



interface AuthScreenProps {
  onBack?: () => void; // optional handler, e.g., for a back button
  onPlayAsGuest?: () => void; // optional handler for guest mode (mobile only)
  [key: string]: unknown;  // allows passing attributes like `data-oid`
}

const { width: screenWidth } = Dimensions.get("window");

export default function AuthScreen({ onBack, onPlayAsGuest: _onPlayAsGuest }: AuthScreenProps) {
  const { colors, isDark } = useAppTheme();
  const styles = createAuthStyles(colors);
  const reduceMotion = useReducedMotion();
  const { getGuestDataForMigration, clearGuestData, isGuest } = useGuestUser();

  // Animation values
  const logoAnim = useRef(new Animated.Value(0)).current;
  const cardAnim = useRef(new Animated.Value(0)).current;
  const [buttonPressed, setButtonPressed] = useState(false);
  const [socialPressed, setSocialPressed] = useState(false);
  const shimmerAnim = useRef(new Animated.Value(0)).current;

  // Auth state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mode, setMode] = useState<"login" | "signup">("login");
  const { language: globalLanguage, setLanguage: setGlobalLanguage, detectDeviceLanguage } = useLanguageStore();
  const [language, setLanguage] = useState(globalLanguage || detectDeviceLanguage());
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false); // email/password flows
  const [googleLoading, setGoogleLoading] = useState(false);
  const [appleLoading, setAppleLoading] = useState(false);
  const [name, setName] = useState("");
  const [emailFocused, setEmailFocused] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [showEmailConfirmation, setShowEmailConfirmation] = useState(false);
  const [confirmationEmail, setConfirmationEmail] = useState("");

  // Handle Android hardware back button
  useEffect(() => {
    if (Platform.OS === 'android') {
      const backHandler = BackHandler.addEventListener('hardwareBackPress', () => {
        if (onBack) {
          onBack();
          return true; // Prevent default behavior
        }
        return false; // Let default behavior happen
      });

      return () => backHandler.remove();
    }
  }, [onBack]);

  // Sync language with global language store
  useEffect(() => {
    if (globalLanguage && globalLanguage !== language) {
      setLanguage(globalLanguage);
    }
  }, [globalLanguage]);

  // Check for OAuth tracking data on component mount (web only)
  useEffect(() => {
    // Only run OAuth tracking on web platform
    if (Platform.OS === 'web' && typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
      const oauthAttempt = localStorage.getItem('oauth_attempt');
      if (oauthAttempt) {
        try {
          const attemptData = JSON.parse(oauthAttempt);
          console.log('🔍 [OAuth Tracker] Found OAuth attempt data:', attemptData);
          
          // Check if this was a recent attempt (within last 5 minutes)
          const attemptTime = new Date(attemptData.timestamp);
          const now = new Date();
          const timeDiff = (now.getTime() - attemptTime.getTime()) / 1000 / 60; // minutes
          
          if (timeDiff < 5) {
            console.log('🔍 [OAuth Tracker] Recent OAuth attempt detected:', {
              step: attemptData.step,
              timeAgo: `${Math.round(timeDiff)} minutes ago`,
              platform: attemptData.platform
            });
            
            // Update localStorage to show we're now in callback phase
            localStorage.setItem('oauth_attempt', JSON.stringify({
              ...attemptData,
              callbackReceived: true,
              callbackTimestamp: new Date().toISOString()
            }));
          }
        } catch (e) {
          console.error('🔍 [OAuth Tracker] Error parsing OAuth attempt data:', e);
        }
      }
      
      // Also check if current URL contains OAuth callback parameters
      const currentUrl = window.location.href;
      if (currentUrl.includes('?code=') || currentUrl.includes('&code=')) {
        console.log('🔍 [OAuth Tracker] OAuth callback URL detected:', redactUrl(currentUrl));
        localStorage.setItem('oauth_callback_url', currentUrl);
      }
    }
  }, []);

  useEffect(() => {
    if (reduceMotion) {
      logoAnim.setValue(1);
      cardAnim.setValue(1);
      return;
    }
    Animated.stagger(150, [
      Animated.timing(logoAnim, {
        toValue: 1,
        duration: 650,
        useNativeDriver: true,
        easing: Easing.out(Easing.exp),
      }),
      Animated.timing(cardAnim, {
        toValue: 1,
        duration: 650,
        useNativeDriver: true,
        easing: Easing.out(Easing.exp),
      }),
    ]).start();
  }, [reduceMotion]);

  // Shimmer animation loop
  useEffect(() => {
    if (reduceMotion) return;
    Animated.loop(
      Animated.timing(shimmerAnim, {
        toValue: 1,
        duration: 1500,
        useNativeDriver: true,
        easing: Easing.linear,
      })
    ).start();
  }, [reduceMotion]);

  // Button animated styles
  const buttonScale = buttonPressed ? 0.96 : 1;
  const socialScale = socialPressed ? 0.96 : 1;
  const buttonShadow = buttonPressed ? 10 : 24;
  const socialShadow = socialPressed ? 10 : 24;

  // Shimmer translateX (using numeric values for React Native compatibility)
  const shimmerTranslate = shimmerAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [-screenWidth * 0.6, screenWidth * 1.2],
  });

  // Helper function to migrate guest data after successful auth
  const migrateGuestDataIfNeeded = async () => {
    if (isGuest) {
      try {
        const guestData = await getGuestDataForMigration();
        if (guestData && guestData.quizzesCompleted > 0) {
          console.log('📦 [Auth] Migrating guest data after authentication');
          await authService.migrateGuestData(guestData);
          await clearGuestData();
          console.log('✅ [Auth] Guest data migration complete');
        }
      } catch (error) {
        console.error('❌ [Auth] Guest data migration failed:', error);
        // Don't block auth flow if migration fails
      }
    }
  };

  const handleAuth = async () => {
    setError("");
    setLoading(true);

    try {
      if (mode === "login") {
        // Login flow - use authService
        const user = await authService.signIn({ email, password });
        console.log('✅ [Auth] Login successful');

        // Migrate guest data if coming from guest mode
        await migrateGuestDataIfNeeded();

        // User will be redirected by AuthGuard after successful login
      } else {
        // Signup flow - use authService which handles email confirmation
        const result = await authService.signUp({
          email: email.trim(),
          password,
          name: name.trim(),
          language
        });

        if (result.needsConfirmation) {
          // Show email confirmation UI instead of error
          console.log('📧 [Auth] Email confirmation required');
          setConfirmationEmail(email);
          setShowEmailConfirmation(true);
          setError(""); // Clear any errors
          setLoading(false);

          // On mobile, show an alert as well
          if (Platform.OS !== 'web') {
            Alert.alert(
              uiText('checkEmailTitle'),
              uiText('checkEmailMessage', { email }),
              [{ text: uiText('ok') }]
            );
          }
          return;
        }

        // If signup succeeded without confirmation (shouldn't normally happen)
        console.log('✅ [Auth] Signup successful without confirmation');
      }
    } catch (e: unknown) {
      console.error('❌ [Auth] Authentication error:', e);
      setError(e instanceof Error ? e.message : "Unknown error occurred");
    } finally {
      setLoading(false);
    }
  };

  const handleResendConfirmation = async () => {
    setError("");
    setLoading(true);

    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: confirmationEmail,
      });

      if (error) throw error;

      if (Platform.OS === 'web') {
        setError(uiText('emailSentBanner'));
      } else {
        Alert.alert(
          uiText('emailSentTitle'),
          uiText('emailSentMessage'),
          [{ text: uiText('ok') }]
        );
      }
    } catch (error: unknown) {
      console.error('❌ [Auth] Resend confirmation error:', error);
      setError(error instanceof Error ? error.message : "Failed to resend email");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError("");
    setGoogleLoading(true);

    try {
      console.log('🚀 [Google OAuth] Starting Google sign-in...');

      // Use the improved auth service
      const user = await authService.signInWithGoogle();

      if (user) {
        console.log('✅ [Google OAuth] Sign-in successful');

        // Migrate guest data if coming from guest mode
        await migrateGuestDataIfNeeded();

        // User will be automatically redirected by AuthGuard
      } else {
        console.log('🌐 [Google OAuth] Redirecting to Google...');
        // For web, the page will redirect, so we don't need to do anything
      }
    } catch (error: unknown) {
      console.error('❌ [Google OAuth] Sign-in failed:', error);
      const errorMessage = error instanceof Error ? error.message : '';

      // Don't show error if it's a transient session establishment error
      // The session might still be established asynchronously
      const isTransientError =
        errorMessage.includes('OAuth session could not be established') ||
        errorMessage.includes('Unacceptable audience') ||
        errorMessage.includes('credentials issue') ||
        errorMessage.includes('Invalid API Key');

      if (!isTransientError) {
        setError(errorMessage || 'Failed to sign in with Google. Please try again.');
      } else {
        console.log('⚠️ [Google OAuth] Transient error during session establishment - session may still succeed');
        // Clear loading state after a delay to allow session to establish
        setTimeout(() => {
          setGoogleLoading(false);
        }, 2000);
        return; // Don't set error or stop loading immediately
      }
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleAppleSignIn = async () => {
    setError("");
    setAppleLoading(true);

    try {
      console.log('🍎 [Apple OAuth] Starting Apple sign-in...');

      const user = await authService.signInWithApple();

      if (user) {
        console.log('✅ [Apple OAuth] Sign-in successful');

        // Migrate guest data if coming from guest mode
        await migrateGuestDataIfNeeded();

        // User will be automatically redirected by AuthGuard
      } else {
        console.log('🌐 [Apple OAuth] Redirecting to Apple...');
        // For web, the page will redirect, so we don't need to do anything
      }
    } catch (error: unknown) {
      console.error('❌ [Apple OAuth] Sign-in failed:', error);
      const errorMessage = error instanceof Error ? error.message : '';
      const errorCode = typeof error === 'object' && error !== null && 'code' in error
        ? String((error as { code?: unknown }).code)
        : '';

      // Don't show error if user just canceled or if it's ERR_CANCELED
      const isCancelError =
        errorMessage === 'Apple Sign In was canceled' ||
        errorCode === 'ERR_CANCELED' ||
        errorMessage.includes('cancel');

      // Don't show error if it's a transient session establishment error
      const isTransientError =
        errorMessage.includes('OAuth session could not be established') ||
        errorMessage.includes('Unacceptable audience') ||
        errorMessage.includes('credentials issue') ||
        errorMessage.includes('Invalid API Key');

      if (!isCancelError && !isTransientError) {
        setError(errorMessage || 'Failed to sign in with Apple. Please try again.');
      } else if (isTransientError) {
        console.log('⚠️ [Apple OAuth] Transient error during session establishment - session may still succeed');
        // Clear loading state after a delay to allow session to establish
        setTimeout(() => {
          setAppleLoading(false);
        }, 2000);
        return; // Don't set error or stop loading immediately
      }
    } finally {
      setAppleLoading(false);
    }
  };

  return (
    <View style={{ flex: 1, backgroundColor: colors.surface }}>
      <ScreenWrapper keyboardAware style={styles.container}>
        {/* Back button - only show if onBack handler is provided */}
        {onBack && (
          <TouchableOpacity
            onPress={onBack}
            style={{
              position: 'absolute',
              top: Platform.OS === 'ios' ? 50 : 20,
              left: 20,
              zIndex: 10,
              width: 40,
              height: 40,
              borderRadius: 20,
              backgroundColor: colors.surfaceContainerHigh,
              justifyContent: 'center',
              alignItems: 'center',
              shadowColor: colors.black,
              shadowOffset: { width: 0, height: 2 },
              shadowOpacity: isDark ? 0.14 : 0.04,
              shadowRadius: 12,
              elevation: 2,
              borderWidth: 1,
              borderColor: colors.outlineVariant,
            }}
          >
            <Ionicons name="arrow-back" size={24} color={colors.onSurface} />
          </TouchableOpacity>
        )}

        <Animated.ScrollView
          contentContainerStyle={{ flexGrow: 1, justifyContent: 'flex-start', alignItems: 'center', paddingBottom: 60 }}
          keyboardShouldPersistTaps="handled"
          keyboardDismissMode="on-drag"
          showsVerticalScrollIndicator={true}
          bounces={true}
        >
          {/* Top: LogoHeader */}
          <LogoHeader logoAnim={logoAnim} mode={mode} />

          {/* Social Sign In Buttons */}
          <View style={{ width: '90%', maxWidth: 400, marginBottom: 16, gap: 12 }}>
            {/* Apple Sign In Button - iOS and Web */}
            <AppleSignInButton
              onPress={handleAppleSignIn}
              loading={appleLoading}
              onPressIn={() => setSocialPressed(true)}
              onPressOut={() => setSocialPressed(false)}
              socialScale={socialScale}
            />

            {/* Google Sign In Button */}
            <SocialButton
              loading={googleLoading}
              onPress={handleGoogleSignIn}
              onPressIn={() => setSocialPressed(true)}
              onPressOut={() => setSocialPressed(false)}
              socialScale={socialScale}
              socialShadow={socialShadow}
            />
          </View>

          {/* Glassmorphic card with AuthForm or Email Confirmation */}
          <Animated.View style={[styles.formContainer, { opacity: cardAnim, transform: [{ translateY: cardAnim.interpolate({ inputRange: [0, 1], outputRange: [40, 0] }) }] }]}>
            {showEmailConfirmation ? (
              <EmailConfirmationMessage
                email={confirmationEmail}
                onResend={handleResendConfirmation}
                onBackToLogin={() => {
                  setShowEmailConfirmation(false);
                  setMode('login');
                }}
                loading={loading}
                error={error}
              />
            ) : (
              <AuthForm
                mode={mode}
                setMode={setMode}
                email={email}
                setEmail={setEmail}
                password={password}
                setPassword={setPassword}
                name={name}
                setName={setName}
                language={language}
                setLanguage={setLanguage}
                setGlobalLanguage={setGlobalLanguage}
                error={error}
                loading={loading}
                emailFocused={emailFocused}
                setEmailFocused={setEmailFocused}
                passwordFocused={passwordFocused}
                setPasswordFocused={setPasswordFocused}
                handleAuth={handleAuth}
                buttonScale={buttonScale}
                buttonShadow={buttonShadow}
                shimmerTranslate={shimmerTranslate}
                reduceMotion={reduceMotion}
                setButtonPressed={setButtonPressed}
              />
            )}
          </Animated.View>

          {/* Footer */}
          <Footer />
        </Animated.ScrollView>
      </ScreenWrapper>
    </View>
  );
}
